import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { getUserBoards } from '../lib/proxy-client';
import { initVKApp, getVKUser, inviteFriends } from '../lib/vk-bridge';
import { Link, useNavigate } from 'react-router-dom';
import Footer from './Footer';
import Onboarding from './Onboarding';

type SimpleBoard = {
  id: string;
  name: string;
  total_amount: number | null;
  created_at: string;
  restaurant?: { name?: string } | null;
  restaurant_name?: string;
  is_creator?: boolean;
};

type BoardProgress = {
  totalItems: number;
  claimedItems: number;
  totalAmount: number;
  claimedAmount: number;
};

const ONBOARDING_KEY = 'receipt_splitter_onboarding_done';

const MyBoards: React.FC = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [boards, setBoards] = useState<SimpleBoard[]>([]);
  const [, setVkUserId] = useState<number | null>(null);
  const [activeTab, setActiveTab] = useState<'created' | 'invited'>('created');
  const [progressMap, setProgressMap] = useState<Record<string, BoardProgress>>({});
  const [showOnboarding, setShowOnboarding] = useState(() => {
    return !localStorage.getItem(ONBOARDING_KEY);
  });

  const handleOnboardingComplete = () => {
    localStorage.setItem(ONBOARDING_KEY, '1');
    setShowOnboarding(false);
  };

  const handleShare = (boardId: string, title: string) => {
    inviteFriends(boardId, title);
  };

  useEffect(() => {
    const init = async () => {
      await initVKApp();
      const user = await getVKUser();
      const userId = user?.id || null;
      setVkUserId(userId);

      console.log('🔍 MyBoards: VK user ID:', userId);

      try {
        setLoading(true);
        setError(null);

        if (!userId) {
          const isLocal = window.location.hostname === 'localhost';
          if (!isLocal) {
            supabase.from('debug_logs').insert({
              user_id: 0,
              message: 'No VK ID found',
              meta: { step: 'init', platform: 'vk' }
            }).then(() => {});
            setError('Не удалось получить данные пользователя. Попробуйте перезапустить Mini App.');
            setLoading(false);
            return;
          }
        }

        if (userId) {
          console.log('🔍 MyBoards: Loading boards via proxy for VK user:', userId);
          try {
            const rpcData = await getUserBoards(userId);
            if (rpcData) {
              console.log('✅ MyBoards: Loaded via proxy:', rpcData.length);
              setBoards(rpcData as SimpleBoard[]);
              setLoading(false);
              return;
            }
          } catch (rpcError: any) {
            console.warn('⚠️ MyBoards: Proxy failed, falling back.', rpcError);
          }
        }

        // FALLBACK: прямой запрос к Supabase
        if (userId) {
          const { data: profile } = await supabase
            .from('profiles')
            .select('id')
            .eq('telegram_id', userId) // telegram_id хранит VK ID
            .single();

          if (!profile) {
            setBoards([]);
            setLoading(false);
            return;
          }

          const { data: participants } = await supabase
            .from('participants')
            .select('board_id, is_creator')
            .eq('profile_id', profile.id);

          const boardIds = Array.from(new Set((participants || []).map(p => p.board_id)));

          if (boardIds.length === 0) {
            setBoards([]);
            setLoading(false);
            return;
          }

          const { data: boardsData, error: bErr } = await supabase
            .from('boards')
            .select('id, name, total_amount, created_at, restaurant_name, restaurant:restaurants(name)')
            .in('id', boardIds)
            .order('created_at', { ascending: false });

          if (bErr) throw bErr;

          const boardsWithCreatorInfo = (boardsData || []).map(board => {
            const participant = participants?.find(p => p.board_id === board.id);
            return { ...board, is_creator: participant?.is_creator || false };
          });

          setBoards(boardsWithCreatorInfo as SimpleBoard[]);
        }
      } catch (e: any) {
        setError(e?.message || 'Ошибка загрузки досок');
      } finally {
        setLoading(false);
      }
    };

    init();
  }, []);

  // Загрузка прогресса для всех досок
  useEffect(() => {
    if (boards.length === 0) return;
    const loadProgress = async () => {
      const boardIds = boards.map(b => b.id);
      try {
        // Загружаем позиции для всех досок
        const { data: items } = await supabase
          .from('bill_items')
          .select('id, board_id, price')
          .in('board_id', boardIds);

        if (!items || items.length === 0) return;

        // Загружаем выборы
        const itemIds = items.map(i => i.id);
        const { data: selections } = await supabase
          .from('item_selections')
          .select('item_id')
          .in('item_id', itemIds);

        const claimedItemIds = new Set((selections || []).map(s => s.item_id));

        // Считаем прогресс по каждой доске
        const map: Record<string, BoardProgress> = {};
        for (const boardId of boardIds) {
          const boardItems = items.filter(i => i.board_id === boardId);
          const claimed = boardItems.filter(i => claimedItemIds.has(i.id));
          map[boardId] = {
            totalItems: boardItems.length,
            claimedItems: claimed.length,
            totalAmount: boardItems.reduce((s, i) => s + (i.price || 0), 0),
            claimedAmount: claimed.reduce((s, i) => s + (i.price || 0), 0),
          };
        }
        setProgressMap(map);
      } catch (e) {
        console.warn('Progress load error:', e);
      }
    };
    loadProgress();
  }, [boards]);

  if (loading) {
    return <div style={{ padding: 16 }}>Загрузка...</div>;
  }

  if (error) {
    return (
      <div style={{ padding: 16 }}>
        <h3>ReceiptSplitter</h3>
        <p>{error}</p>
      </div>
    );
  }

  const createdBoards = boards.filter(b => b.is_creator);
  const invitedBoards = boards.filter(b => !b.is_creator);

  const tabStyle = {
    flex: 1, padding: '10px', textAlign: 'center' as const,
    borderRadius: '8px', cursor: 'pointer', fontSize: '14px',
    fontWeight: 600, transition: 'all 0.2s ease', userSelect: 'none' as const,
  };
  const activeTabStyle = {
    ...tabStyle,
    background: 'var(--vk-accent, #4680C2)',
    color: '#ffffff',
    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
  };
  const inactiveTabStyle = {
    ...tabStyle,
    background: 'transparent',
    color: 'var(--vk-hint, #999999)',
  };

  const renderProgressBar = (boardId: string) => {
    const progress = progressMap[boardId];
    if (!progress || progress.totalItems === 0) return null;

    const pct = Math.round((progress.claimedItems / progress.totalItems) * 100);
    const isComplete = pct === 100;
    const remaining = progress.totalAmount - progress.claimedAmount;

    return (
      <div style={{ marginTop: 10 }}>
        {/* Progress bar */}
        <div style={{
          height: 6, borderRadius: 3, background: 'var(--vk-secondary-bg, #e9ecef)',
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%', borderRadius: 3,
            width: `${pct}%`,
            background: isComplete
              ? 'linear-gradient(90deg, #34c759, #30d158)'
              : 'linear-gradient(90deg, #4680C2, #5a9fd4)',
            transition: 'width 0.5s ease',
          }} />
        </div>
        {/* Label */}
        <div style={{
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          marginTop: 5, fontSize: 12, color: 'var(--vk-hint, #999)',
        }}>
          {isComplete ? (
            <span style={{ color: '#34c759', fontWeight: 600 }}>✅ Все позиции распределены</span>
          ) : (
            <>
              <span>{progress.claimedItems} из {progress.totalItems} позиций</span>
              <span style={{ fontWeight: 600, color: 'var(--vk-text, #333)' }}>
                осталось {remaining.toLocaleString('ru-RU')}₽
              </span>
            </>
          )}
        </div>
      </div>
    );
  };

  const renderBoardCard = (b: SimpleBoard, showShare: boolean) => (
    <div key={b.id} style={{
      border: '1px solid var(--vk-separator, #e9ecef)',
      borderRadius: 12, padding: 14,
      background: 'var(--vk-card, #ffffff)',
      color: 'var(--vk-text, #000000)'
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8 }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: 16 }}>
            {b.restaurant_name || b.restaurant?.name || b.name || 'Без названия'}
          </div>
          <div style={{ color: 'var(--vk-hint, #999)', fontSize: 13, marginTop: 2 }}>
            {new Date(b.created_at).toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' })}
            {' • '}
            {new Date(b.created_at).toLocaleDateString('ru-RU')}
          </div>
          <div style={{ fontSize: 15, marginTop: 4, fontWeight: 500 }}>
            {(b.total_amount ?? 0).toLocaleString('ru-RU')}₽
          </div>
        </div>
      </div>

      {renderProgressBar(b.id)}

      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginTop: 12 }}>
        <Link to={`/board/${b.id}`} style={{
          padding: '8px 14px', background: 'var(--vk-accent, #4680C2)',
          color: '#fff', borderRadius: 8, textDecoration: 'none', fontSize: 14, fontWeight: 500
        }}>
          📱 Открыть
        </Link>
        {showShare && (
          <button onClick={() => handleShare(b.id, b.restaurant_name || b.restaurant?.name || b.name || '')} style={{
            padding: '8px 14px', background: 'var(--vk-accent, #4680C2)',
            color: '#fff', border: 'none', borderRadius: 8, fontSize: 14, cursor: 'pointer', fontWeight: 500
          }}>
            📤 Поделиться
          </button>
        )}
      </div>
    </div>
  );

  if (showOnboarding) {
    return <Onboarding onComplete={handleOnboardingComplete} />;
  }

  return (
    <div style={{ padding: 16 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, margin: 0 }}>Мои чеки</h1>
        <button
          onClick={() => navigate('/upload')}
          style={{
            padding: '8px 16px', background: 'var(--vk-accent, #4680C2)',
            color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600,
            fontSize: 14, cursor: 'pointer'
          }}
        >
          📸 Загрузить чек
        </button>
      </div>

      {/* Вкладки */}
      <div style={{ display: 'flex', background: 'var(--vk-secondary-bg, #eef1f5)', padding: '4px', borderRadius: '12px', marginBottom: '20px' }}>
        <div style={activeTab === 'created' ? activeTabStyle : inactiveTabStyle} onClick={() => setActiveTab('created')}>
          🏗️ Мои{createdBoards.length > 0 && (
            <span style={{
              marginLeft: 6, background: activeTab === 'created' ? 'rgba(255,255,255,0.3)' : 'var(--vk-accent, #4680C2)',
              color: activeTab === 'created' ? '#fff' : '#fff',
              borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700,
            }}>{createdBoards.length}</span>
          )}
        </div>
        <div style={activeTab === 'invited' ? activeTabStyle : inactiveTabStyle} onClick={() => setActiveTab('invited')}>
          📨 Приглашения{invitedBoards.length > 0 && (
            <span style={{
              marginLeft: 6, background: activeTab === 'invited' ? 'rgba(255,255,255,0.3)' : 'var(--vk-accent, #4680C2)',
              color: activeTab === 'invited' ? '#fff' : '#fff',
              borderRadius: 10, padding: '1px 7px', fontSize: 12, fontWeight: 700,
            }}>{invitedBoards.length}</span>
          )}
        </div>
      </div>
      
      {boards.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px 20px' }}>
          <div style={{ fontSize: 48, marginBottom: 16 }}>📸</div>
          <p style={{ color: 'var(--vk-hint)', fontSize: 16 }}>
            У вас пока нет досок.<br/>
            Загрузите фото чека, чтобы создать первую!
          </p>
          <button
            onClick={() => navigate('/upload')}
            style={{
              marginTop: 16, padding: '14px 28px',
              background: 'var(--vk-accent, #4680C2)',
              color: '#fff', border: 'none', borderRadius: 12,
              fontWeight: 600, fontSize: 16, cursor: 'pointer'
            }}
          >
            Загрузить чек
          </button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {activeTab === 'created' && (
            createdBoards.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--vk-hint)' }}>Нет созданных досок</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {createdBoards.map(b => renderBoardCard(b, true))}
              </div>
            )
          )}
          {activeTab === 'invited' && (
            invitedBoards.length === 0 ? (
              <p style={{ textAlign: 'center', color: 'var(--vk-hint)' }}>Нет приглашений</p>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {invitedBoards.map(b => renderBoardCard(b, false))}
              </div>
            )
          )}
        </div>
      )}
      
      <Footer />
    </div>
  );
};

export default MyBoards;
